import cn from 'classnames';
import Icon from '../icon';
import Icons from '../../types/icons';
import { EMPTY_OBJECT, isNullish } from '../../utility';
import type { ComponentChildren } from 'preact';
import type { ExtendedSize } from '../../types';
import type { TypographyVariant } from '../../types/common';
import type { HTMLAttributes } from '../../types/utility';

type Item = ComponentChildren;

type ListItems = Item[];

type DescriptionListTypography = {
  termTypography?: TypographyVariant | 'visually-hidden' | 'visually-maintain-layout';
  descriptionTypography?: TypographyVariant;
};

type DescriptionProps = {
  value: Item;
  className?: string;
  hasSeparator?: boolean;
  tooltip?: string;
  tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
  term?: ComponentChildren;
} & DescriptionListTypography;

type DescriptionListProps = {
  as: 'dl';
  items: Record<string, Item> | Record<string, DescriptionProps>;
} & DescriptionListTypography &
  HTMLAttributes<HTMLDListElement>;

type OrderedListProps = { as: 'ol'; items: ListItems } & HTMLAttributes<HTMLOListElement>;

type UnorderedListProps = {
  as?: 'ul' | 'menu';
  items: ListItems;
} & HTMLAttributes<HTMLUListElement>;

type ListStyleType =
  | 'none'
  | 'disc'
  | 'circle'
  | 'square'
  | 'decimal'
  | 'decimal-leading-zero'
  | 'lower-roman'
  | 'upper-roman'
  | 'lower-greek'
  | 'armenian'
  | 'georgian'
  | 'lower-alpha'
  | 'upper-alpha'
  | 'cjk-ideographic'
  | 'hebrew'
  | 'hiragana'
  | 'katakana'
  | 'hiragana-iroha'
  | 'katakana-iroha';

type BoxType =
  | 'unbounded'
  | 'bounded'
  | 'bounded-primary'
  | 'unbounded-secondary'
  | 'unbounded-separated';

type ListProps = (OrderedListProps | UnorderedListProps | DescriptionListProps) & {
  basis?: string;
  boxType?: BoxType;
  columns?: number;
  filterEmpty?: boolean;
  gap?: ExtendedSize;
  itemBoxType?: Exclude<BoxType, 'unbounded-separated'> | 'tag';
  marker?:
    | 'none'
    | { type: 'string'; value: string }
    | { type: 'icon'; value: (typeof Icons)[number] }
    | { type: 'css'; value: ListStyleType };
  orientation?:
    | 'horizontal'
    | 'vertical'
    | 'inline-horizontal'
    | 'inline-vertical'
    | 'inline-vertical-table';
  termPosition?: 'before' | 'after';
  wrap?: boolean;
};

/*
  TODO: think of a better way to generate a unique key
  This should be straightforward for primitive items, but a little more complex for non-primitive items
  Object.entries allows us to "skirt" around the lint rule of avoiding using array indexes as keys
*/
const renderListItems = (items: ListItems, icon?: (typeof Icons)[number]) => {
  return Object.entries(items).map(([key, item]) => (
    <li key={key} data-icon={icon}>
      {item}
    </li>
  ));
};

const getMarkerStyle = (marker: ListProps['marker']) => {
  if (!marker) {
    return;
  } else if (marker === 'none' || marker.type === 'icon') {
    return { '--list-style-type': 'none' };
  } else if (marker.type === 'css') {
    return { '--list-style-type': marker.value };
  } else if (marker.type === 'string') {
    return { '--marker': `'${marker.value}'` };
  }
};

export default function List({
  as,
  basis,
  boxType = 'unbounded',
  className = '',
  columns,
  filterEmpty = false,
  gap,
  items,
  itemBoxType,
  marker,
  orientation,
  termPosition = 'before',
  wrap,
  ...listProps
}: ListProps) {
  const hasCustomMarker = typeof marker === 'object';
  const isMarkerIcon = hasCustomMarker && marker.type === 'icon' && Icons.includes(marker.value);
  const style = getMarkerStyle(marker);

  const hasGap = !!gap;

  const classList =
    cn(className, {
      [orientation || '']:
        as === 'menu'
          ? orientation !== 'horizontal'
          : orientation !== 'vertical' || (orientation === 'vertical' && hasGap),
      ['has-marker']: !!style,
      [`box-type-${boxType}`]: boxType !== 'unbounded',
      [`term-position-after`]: termPosition === 'after',
      [`wrap`]: wrap,
      [`gap-${gap}`]: hasGap,
    }) || '';

  if (as === 'dl') {
    const { termTypography, descriptionTypography, ...dlProps } = listProps as DescriptionListProps;

    return (
      <dl
        style={{
          ...(style || EMPTY_OBJECT),
          '--list-basis': basis,
          '--list-columns': columns,
        }}
        {...dlProps}
        className={classList}
      >
        {Object.entries(items)
          .filter(([_, value]) => (filterEmpty ? !isNullish(value) && value !== '' : true))
          .map(([key, value]) => {
            let term: ComponentChildren = key;
            let termClassName = termTypography;
            let descriptionClassName = descriptionTypography;
            let description = value;
            let groupClassName;
            let tooltip;
            let tooltipPosition;

            if (typeof value === 'object' && value && 'value' in value) {
              const {
                value: desc,
                hasSeparator = false,
                className,
                termTypography,
                descriptionTypography,
                term: passedTerm,
              } = value as DescriptionProps;

              groupClassName = cn(className, { separated: hasSeparator }) || undefined;
              termClassName = termTypography ?? termClassName;
              descriptionClassName = descriptionTypography ?? descriptionClassName;
              description = desc;
              tooltip = (value as DescriptionProps).tooltip;
              tooltipPosition = (value as DescriptionProps).tooltipPosition;

              if (passedTerm) {
                term = passedTerm;
              }
            }

            return (
              <div
                key={key}
                className={
                  cn(groupClassName, {
                    tag: itemBoxType === 'tag',
                    [`item-box-type-${itemBoxType}`]: itemBoxType && itemBoxType !== 'tag',
                  }) || undefined
                }
              >
                <dt className={termClassName} data-icon={isMarkerIcon ? marker.value : undefined}>
                  {term}
                  {tooltip ? (
                    <Icon
                      as="span"
                      className="tooltip"
                      icon="info"
                      title={tooltip}
                      data-tooltip-position={tooltipPosition}
                    />
                  ) : undefined}
                </dt>
                <dd className={descriptionClassName}>{description}</dd>
              </div>
            );
          })}
      </dl>
    );
  }

  const markerIcon = isMarkerIcon ? marker.value : undefined;
  if (as === 'ol') {
    return (
      <ol style={style} {...(listProps as HTMLAttributes<HTMLOListElement>)} className={classList}>
        {renderListItems(items, markerIcon)}
      </ol>
    );
  }

  if (as === 'menu') {
    return (
      <menu style={style} {...(listProps as HTMLAttributes<HTMLMenuElement>)} className={classList}>
        {renderListItems(items, markerIcon)}
      </menu>
    );
  }

  return (
    <ul style={style} {...(listProps as HTMLAttributes<HTMLUListElement>)} className={classList}>
      {renderListItems(items, markerIcon)}
    </ul>
  );
}

export function DescriptionList(props: Omit<Extract<ListProps, DescriptionListProps>, 'as'>) {
  return <List as="dl" {...props} />;
}

export function OrderedList(props: Omit<Extract<ListProps, OrderedListProps>, 'as'>) {
  return <List as="ol" {...props} />;
}

export function UnorderedList(props: Omit<Extract<ListProps, UnorderedListProps>, 'as'>) {
  return <List as="ul" {...props} />;
}

export function MenuList(props: Omit<Extract<ListProps, UnorderedListProps>, 'as'>) {
  return <List as="menu" orientation="horizontal" {...props} />;
}
